// Thin shim wrapping ppu-paddle-ocr (PP-OCRv5 English mobile) so Capture.js
// can keep the TextDetector-shaped call sites. Backend selection mirrors
// scratch/paddle-smoke/ocr.js: WebGPU -> WASM-threaded -> WASM-single.

import * as ort from "onnxruntime-web";
import {
    PaddleOcrService,
    V5_EN_MOBILE_MODEL,
    isWebGpuAvailable,
} from "ppu-paddle-ocr/web";

const ORT_VERSION = "1.23.2";
ort.env.wasm.wasmPaths = `https://cdn.jsdelivr.net/npm/onnxruntime-web@${ORT_VERSION}/dist/`;

let service = null;
let initPromise = null;
let backend = "unknown";
let ready = false;

export function isReady() {
    return ready;
}

export function getBackend() {
    return backend;
}

export function initOcr() {
    if (initPromise) return initPromise;
    initPromise = (async () => {
        const wantWebGpu = await isWebGpuAvailable().catch(() => false);
        const providers = wantWebGpu ? ["webgpu", "wasm"] : ["wasm"];
        // eslint-disable-next-line no-undef
        const coi = typeof crossOriginIsolated !== "undefined" && crossOriginIsolated;
        backend = wantWebGpu ? "webgpu" : (coi ? "wasm-threaded" : "wasm-single");
        const svc = new PaddleOcrService({
            model: V5_EN_MOBILE_MODEL,
            session: {executionProviders: providers, graphOptimizationLevel: "all"},
        });
        await svc.initialize();
        service = svc;
        ready = true;
        return {backend};
    })();
    return initPromise;
}

// Convert an ImageData / ImageBitmap / HTMLCanvasElement to a canvas that
// ppu-paddle-ocr accepts (its web build takes a CoreCanvas — HTMLCanvasElement
// or OffscreenCanvas).
function toCanvas(source) {
    if (typeof HTMLCanvasElement !== "undefined" && source instanceof HTMLCanvasElement) {
        return source;
    }
    if (typeof OffscreenCanvas !== "undefined" && source instanceof OffscreenCanvas) {
        return source;
    }
    const w = source.width;
    const h = source.height;
    const c = document.createElement("canvas");
    c.width = w;
    c.height = h;
    const cx = c.getContext("2d");
    if (typeof ImageData !== "undefined" && source instanceof ImageData) {
        cx.putImageData(source, 0, 0);
    } else {
        cx.drawImage(source, 0, 0);
    }
    return c;
}

// Returns [{rawValue, boundingBox: {left, top, width, height}}] to match
// the removed TextDetector.detect() shape.
export async function detectText(source) {
    if (!service) {
        throw new Error("OCR service not initialized; call initOcr() first");
    }
    const canvas = toCanvas(source);
    const result = await service.recognize(canvas, {flatten: true});
    const items = result.results || [];
    return items.map(({text, box}) => ({
        rawValue: text,
        boundingBox: {
            left: box.x,
            top: box.y,
            width: box.width,
            height: box.height,
        },
    }));
}
